import React from 'react';
import dayjs from 'dayjs';
import { useHistory } from 'react-router-dom';

import { Check, Loader, Pencil, X } from 'lucide-react';

import {
  Button,
  IconGroup,
  Segment,
  Grid,
  ButtonGroup,
  Checkbox,
  Tooltip,
} from '@mairistem/composants';

import { StatutPacs } from '@/constants';
import { baseUrlApp } from '@/routes';
import type { ConventionDeclaration, Pacs } from '@/types/Pacs';

import Divider from '@/components/Common/Divider';
import useUpdateConvention from '@/hooks/useUpdateConvention';

import InformationsConventionsContainer from './InformationsConventions/InformationsConventionsContainer';

interface InformationsDeclarationsPacsPanelProps {
  conventionsDeclarations?: ConventionDeclaration[];
  derniereConventionStatut?: string;
  derniereConventionEnregistre?: number;
  derniereConventionConforme?: number;
  conventionDeclarationBase: Partial<ConventionDeclaration>;
  showDetails?: boolean;
  pacs?: Pacs;
  onConventionSaved?: (convention: Partial<ConventionDeclaration>) => void;
}

const InformationsDeclarationsPacsPanel = (
  props: InformationsDeclarationsPacsPanelProps,
) => {
  const {
    conventionsDeclarations,
    derniereConventionStatut,
    derniereConventionEnregistre,
    conventionDeclarationBase,
    showDetails,
    pacs,
    onConventionSaved,
  } = props;

  const history = useHistory();
  const { updateConvention, loading: savingConvention } = useUpdateConvention();

  const [selectedConvention, setSelectedConvention] = React.useState<any>(
    conventionDeclarationBase,
  );

  // Permet de mettre à jour l'affichage sans avoir à recharger la page
  const [editionRegistreLocal, setEditionRegistreLocal] = React.useState<
    Record<number, number>
  >({});

  React.useEffect(() => {
    setSelectedConvention(conventionDeclarationBase);
    setEditionRegistreLocal({});
  }, [conventionDeclarationBase]);

  const conforme = selectedConvention?.conforme;
  const enregistre = selectedConvention?.editionRegistre ?? 0;

  // Seule la convention initiale (projet ou conclusion) peut venir d'une
  // demande HubEE : les modifications et la dissolution n'ont pas de lien.
  const identifiantTeledossier =
    selectedConvention?.action === 'Conclusion'
      ? selectedConvention?.identifiantTeledossier
      : null;

  const derniereConventionId = conventionsDeclarations?.[
    conventionsDeclarations.length - 1
  ]?.identifiant as number | undefined;

  const derniereEnregistre =
    derniereConventionId != null && derniereConventionId in editionRegistreLocal
      ? editionRegistreLocal[derniereConventionId]
      : derniereConventionEnregistre;

  // Impossible sur un Projet, un Pacs dissous ou irrecevable ; sinon la dernière convention doit être enregistrée
  const ajoutImpossible =
    derniereConventionStatut === StatutPacs.PROJET ||
    derniereConventionStatut === StatutPacs.DISSOUS ||
    derniereConventionStatut === StatutPacs.IRRECEVABLE;
  const peutAjouterConvention = !ajoutImpossible && !!derniereEnregistre;

  const handleConventionClick = (convention: any) => {
    const local = editionRegistreLocal[convention.identifiant];
    setSelectedConvention(
      local === undefined
        ? convention
        : { ...convention, editionRegistre: local },
    );
  };

  const handleChangeEditionType = React.useCallback(
    (value: number) => {
      if (!selectedConvention?.identifiant || value === conforme) return;

      setSelectedConvention((prev: any) => ({ ...prev, conforme: value }));
      updateConvention(selectedConvention.identifiant, {
        ...selectedConvention,
        conforme: value,
      }).then(() => {
        onConventionSaved?.({
          identifiant: selectedConvention.identifiant,
          conforme: value,
        });
      });
    },
    [selectedConvention, conforme, updateConvention, onConventionSaved],
  );

  const handleToggleEnregistre = React.useCallback(
    (checked: boolean) => {
      if (!selectedConvention?.identifiant) return;

      const value = checked ? 1 : 0;
      if (value === enregistre) return;

      setSelectedConvention((prev: any) => ({
        ...prev,
        editionRegistre: value,
      }));
      setEditionRegistreLocal((prev) => ({
        ...prev,
        [selectedConvention.identifiant]: value,
      }));
      updateConvention(selectedConvention.identifiant, {
        ...selectedConvention,
        editionRegistre: value,
      }).then(() => {
        onConventionSaved?.({
          identifiant: selectedConvention.identifiant,
          editionRegistre: value,
        });
      });
    },
    [selectedConvention, enregistre, updateConvention, onConventionSaved],
  );

  const getConventionStyle = (action: string, size: number = 10) => {
    switch (action) {
      case 'Conclusion':
        return {
          color: '#1faf89',
          icon: <Check size={size} strokeWidth={3} />,
        };
      case 'Modification':
        return {
          color: '#f07d19',
          icon: <Pencil size={size} strokeWidth={3} />,
        };
      case 'Dissolution':
        return { color: '#c01622', icon: <X size={size} strokeWidth={3} /> };
      default:
        return { color: 'black', icon: <Loader size={size} strokeWidth={3} /> };
    }
  };

  const separateurTirets = (
    <>
      <div
        style={{
          backgroundColor: '#aaaaaa',
          width: 4,
          height: 8,
          zIndex: 2,
        }}
      />
      {Array.from({ length: 6 }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: no need
        <React.Fragment key={`separateurTirets${i}`}>
          <div
            style={{
              backgroundColor: 'white',
              width: 5,
              height: 10,
              zIndex: 2,
            }}
          />
          <div
            style={{
              backgroundColor: '#aaaaaa',
              width: 5,
              height: 8,
              zIndex: 2,
            }}
          />
        </React.Fragment>
      ))}
    </>
  );

  const conventionsList = conventionsDeclarations?.map((convention) => {
    const isSelected =
      selectedConvention?.identifiant === convention.identifiant;
    const { color, icon } = getConventionStyle(convention?.action);

    return (
      <Segment
        basic
        data-testid={`informations-declarations-convention-${convention.identifiant}`}
        onClick={() => handleConventionClick(convention)}
        key={convention.identifiant}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          color: isSelected ? '#004a99' : color,
          cursor: 'pointer',
          margin: 0,
        }}
      >
        {isSelected ? (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              border: '2px solid black',
              width: 20,
              height: 20,
              background: 'white',
            }}
          >
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                width: 12,
                height: 12,
                background: '#004a99',
              }}
            />
          </div>
        ) : (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              border: `2px solid ${color}`,
              width: 20,
              height: 20,
              background: '#fff',
            }}
          >
            {icon}
          </div>
        )}
        {/* Si trop de conventions, on affiche uniquement les icons */}
        {showDetails && (
          <div
            style={{
              position: 'absolute',
              top: '80%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: 'max-content',
              fontWeight: isSelected ? 'bold' : 'normal',
            }}
          >
            <span>
              {dayjs(convention.dateEnregistrement).format('DD/MM/YYYY')}
            </span>
            <span>{convention.action}</span>
          </div>
        )}
      </Segment>
    );
  });

  return (
    <>
      <div
        style={{
          position: 'relative',
          alignItems: 'center',
          justifyContent: 'center',
          display: 'flex',
          marginTop: '1.5em',
          marginBottom: '1.5em',
        }}
      >
        {separateurTirets}
        <Divider
          style={{
            height: 10,
            backgroundColor: '#aaaaaa',
            width: '100%',
            position: 'absolute',
            zIndex: 1,
          }}
        />
        <IconGroup
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-around',
            width: '100%',
            zIndex: 2,
            backgroundColor: 'transparent',
          }}
        >
          {conventionsList}
        </IconGroup>
        <Tooltip
          mouseEnterDelay={100}
          trigger={
            <Button
              size="mini"
              data-testid="informations-declarations-ajouter-convention"
              style={{
                zIndex: 3,
                margin: 0,
              }}
              icon="plus"
              iconSet="Lucide"
              active={peutAjouterConvention}
              disabled={!peutAjouterConvention}
            />
          }
          content={
            ajoutImpossible ? null : (
              <Segment>
                {derniereEnregistre
                  ? 'Ajouter une nouvelle convention'
                  : 'La dernière convention doit être enregistrée au registre pour ajouter une nouvelle convention'}
              </Segment>
            )
          }
        />
      </div>
      <Segment
        style={{
          display: 'flex',
          marginTop: '2.5em',
          flexDirection: 'column',
          paddingLeft: '2.5em',
          paddingRight: '2.5em',
          marginBottom: '1em',
          paddingBottom: '2em',
        }}
      >
        <Grid
          style={{ width: '100%', marginBottom: '1em', flexWrap: 'nowrap' }}
        >
          <Grid.Column style={{ flex: '1 1 50%', minWidth: 0 }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  border: `2px solid ${getConventionStyle(selectedConvention?.action, 20)?.color}`,
                  width: 40,
                  height: 40,
                  color: getConventionStyle(selectedConvention?.action, 20)
                    ?.color,
                }}
              >
                {getConventionStyle(selectedConvention?.action, 20)?.icon}
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  paddingLeft: '1em',
                }}
              >
                <span style={{ fontSize: 15, color: 'black' }}>
                  <b>{selectedConvention?.action} du PACS</b>
                </span>
                <span style={{ color: '#004a99' }}>
                  <b>
                    {dayjs(selectedConvention?.dateEnregistrement).format(
                      'DD/MM/YYYY',
                    )}
                  </b>
                </span>
                {!!identifiantTeledossier && (
                  <Button
                    size="mini"
                    basic
                    data-testid="informations-declarations-lien-hubee"
                    content="Voir la demande HubEE"
                    style={{ marginTop: '0.5em' }}
                    onClick={() =>
                      history.push(
                        `${baseUrlApp}hubee/${identifiantTeledossier}`,
                      )
                    }
                  />
                )}
              </div>
            </div>
          </Grid.Column>
          <Grid.Column
            style={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              width: 'auto',
              minWidth: 'fit-content',
            }}
          >
            <span style={{ whiteSpace: 'nowrap' }}>Type d&apos;édition :</span>
            &nbsp;
            <ButtonGroup size="tiny">
              <Button
                {...(conforme === 1 && { primary: true })}
                data-testid="informations-declarations-type-edition-conforme"
                content="CONFORME"
                disabled={savingConvention}
                onClick={() => handleChangeEditionType(1)}
              />
              <Button
                {...(conforme !== 1 && { primary: true })}
                data-testid="informations-declarations-type-edition-modificative"
                content="MODIFICATIVE"
                disabled={savingConvention}
                onClick={() => handleChangeEditionType(0)}
              />
            </ButtonGroup>
          </Grid.Column>
          <Grid.Column
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              flex: 1,
              width: 'auto',
              minWidth: 'fit-content',
            }}
          >
            <span style={{ whiteSpace: 'nowrap' }}>
              Confirmer l&apos;enregistrement de la déclaration :{' '}
            </span>
            &nbsp;
            <Checkbox
              toggle
              data-testid="informations-declarations-confirmer-enregistrement"
              checked={!!enregistre}
              disabled={savingConvention}
              onChange={(_e: any, data: any) =>
                handleToggleEnregistre(!!data?.checked)
              }
            />
          </Grid.Column>
        </Grid>
        {selectedConvention?.identifiant && (
          <InformationsConventionsContainer
            conventionsDeclarationsId={selectedConvention.identifiant}
            pacs={pacs}
          />
        )}
      </Segment>
    </>
  );
};

export default InformationsDeclarationsPacsPanel;
